import React, { useMemo, useState } from "react";
import { SCENARIOS, runSimulation } from "../lib/data";
import { PROCUREMENT_SCENARIOS, runProcurementSimulation } from "../lib/dataExtras";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import { useRole } from "../lib/role.jsx";
import { WORKBENCH_SCENARIO_ALLOW } from "../lib/users";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, ResponsiveContainer, ComposedChart,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import * as Icons from "lucide-react";
import { useChartColors } from "../lib/chartColors";

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

// Combine the two scenario groups, tagging each with its runner
const ALL_SCENARIOS = [
  ...SCENARIOS.map(s => ({ ...s, group: "Operations & Demand", runner: runSimulation })),
  ...PROCUREMENT_SCENARIOS.map(s => ({ ...s, group: "Purchasing & Lead Time", runner: runProcurementSimulation })),
];

function TT({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular">
          <span className="w-2 h-2" style={{ background: p.color }} />
          <span className="text-muted">{p.name}</span>
          <span className="ml-auto font-semibold">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

function formatOutcome(v, fmt) {
  if (fmt === "peso") return formatPeso(v, { compact: true });
  if (fmt === "pct") return pct(v);
  if (fmt === "num") return formatNumber(v);
  return v;
}

export default function Workbench() {
  const { workbenchGroups, role, user } = useRole();
  // Filter scenarios by allowed groups for the current role. null = all.
  const scenarios = useMemo(() => {
    let list = workbenchGroups === null ? ALL_SCENARIOS : ALL_SCENARIOS.filter(s => workbenchGroups.includes(s.group));
    // Fine-grained per-scenario allow-list (e.g., SOW pilot only sees 3 scenarios)
    const scenarioAllow = user ? WORKBENCH_SCENARIO_ALLOW[user.role] : null;
    if (scenarioAllow) list = list.filter(s => scenarioAllow.includes(s.id));
    return list;
  }, [workbenchGroups, user]);

  const firstScenario = scenarios[0] || ALL_SCENARIOS[0];
  const [active, setActive] = useState(firstScenario);
  const [inputs, setInputs] = useState(() => Object.fromEntries(firstScenario.inputs.map(i => [i.id, i.default])));
  const [results, setResults] = useState(() => firstScenario.runner(firstScenario.id, Object.fromEntries(firstScenario.inputs.map(i => [i.id, i.default]))));
  const [running, setRunning] = useState(false);

  // Reset selection if the active scenario is no longer in the visible set (role change after sign-in).
  React.useEffect(() => {
    if (!scenarios.find(s => s.id === active.id)) {
      const fresh = scenarios[0];
      if (fresh) selectScenario(fresh);
    }
  }, [scenarios]);

  function selectScenario(s) {
    setActive(s);
    const def = Object.fromEntries(s.inputs.map(i => [i.id, i.default]));
    setInputs(def);
    setResults(s.runner(s.id, def));
  }

  function run() {
    setRunning(true);
    setTimeout(() => {
      setResults(active.runner(active.id, inputs));
      setRunning(false);
    }, 700);
  }

  const Icon = Icons[active.icon] || Icons.FlaskConical;
  const C = useChartColors();

  // Group by category (scoped to visible scenarios)
  const groups = scenarios.reduce((acc, s) => {
    acc[s.group] = acc[s.group] || [];
    acc[s.group].push(s);
    return acc;
  }, {});
  const isScoped = workbenchGroups !== null;

  // If the role has zero scenarios, render an empty-state.
  if (scenarios.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <SectionHeader eyebrow="PART VI · Digital Twin" title="Executive Scenario Workbench"
          description="Interactive scenario simulation across the 1Rotary digital twin." />
        <div className="tile p-6 text-center" data-testid="workbench-empty">
          <Badge status="info">Role-scoped</Badge>
          <div className="font-display text-lg mt-3">No simulation scenarios available</div>
          <p className="text-sm text-muted mt-1.5">Your role ({role?.name}) does not have access to the Simulation Workbench scenarios. Contact the Executive Data Council to request access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART VI · Digital Twin" title="Executive Scenario Workbench"
        description="Interactive scenario simulation across the 1Rotary digital twin. Adjust inputs, run the model, review projected outcomes." />

      {isScoped && (
        <div data-testid="workbench-scope-banner" className="tile p-3 flex items-center gap-2.5 text-[12px] border-l-2 border-primary/60 bg-primary/5">
          <Badge status="info">Role-scoped</Badge>
          <span className="text-fg/90">Showing {scenarios.length} of {ALL_SCENARIOS.length} scenarios visible to <span className="font-medium">{role?.name}</span> — categories: {Object.keys(groups).join(", ")}.</span>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Scenarios list */}
        <div className="col-span-12 lg:col-span-3 space-y-3" data-testid="scenario-list">
          {Object.entries(groups).map(([groupName, list]) => (
            <div key={groupName}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-subtle mb-1.5 px-1">{groupName}</div>
              <div className="space-y-2">
                {list.map(s => {
                  const I = Icons[s.icon] || Icons.FlaskConical;
                  const isActive = active.id === s.id;
                  return (
                    <button key={s.id}
                      data-testid={`scenario-${s.id}`}
                      onClick={() => selectScenario(s)}
                      className={cn("w-full text-left tile p-3.5 transition-all",
                        isActive ? "border-primary glow-primary" : "hover:border-border-strong")}>
                      <div className="flex gap-3">
                        <div className={cn("w-9 h-9 rounded-sm flex items-center justify-center shrink-0",
                          isActive ? "bg-primary text-primary-fg" : "bg-surface-2 text-muted")}>
                          <I size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-semibold text-sm">{s.name}</div>
                          <div className="text-[11px] text-muted leading-snug mt-0.5 line-clamp-2">{s.short}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Workbench */}
        <div className="col-span-12 lg:col-span-9 space-y-6">
          <Tile testid="scenario-inputs">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-sm bg-primary text-primary-fg flex items-center justify-center"><Icon size={22} /></div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">Scenario</div>
                <h2 className="font-display text-2xl font-semibold mt-0.5">{active.name}</h2>
                <p className="text-sm text-muted mt-1">{active.short}</p>
              </div>
              <button data-testid="run-simulation" onClick={run} disabled={running}
                className={cn("h-10 px-5 rounded-sm font-semibold text-sm flex items-center gap-2 transition-all",
                  running ? "bg-surface-2 text-muted" : "bg-primary text-primary-fg hover:opacity-90")}>
                {running ? <Icons.Loader2 size={14} className="animate-spin" /> : <Icons.Play size={14} />}
                {running ? "Running..." : "Run Simulation"}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-border">
              {active.inputs.map(inp => (
                <div key={inp.id}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="kpi-label">{inp.label}</label>
                    <span className="font-mono-tabular text-sm font-semibold text-primary">{inputs[inp.id]}{inp.unit}</span>
                  </div>
                  <input
                    type="range"
                    data-testid={`input-${inp.id}`}
                    min={inp.min}
                    max={inp.max}
                    value={inputs[inp.id]}
                    onChange={e => setInputs(v => ({ ...v, [inp.id]: parseInt(e.target.value, 10) }))}
                    className="w-full accent-primary"
                  />
                  <div className="flex justify-between text-[10px] text-subtle font-mono-tabular mt-1">
                    <span>{inp.min}{inp.unit}</span>
                    <span>{inp.max}{inp.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </Tile>

          {/* Outcomes */}
          <div className="tile grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-border" data-testid="scenario-outcomes">
            {results.outcomes.map((o, i) => (
              <div key={i} className="px-4 py-4">
                <div className="kpi-label">{o.label}</div>
                <div className={cn("kpi-value text-2xl mt-2", typeof o.value === "number" && o.value < 0 && o.format !== "pct" && "text-critical")}>
                  {formatOutcome(o.value, o.format)}
                </div>
              </div>
            ))}
          </div>

          {/* Series chart */}
          <Tile title="Projected Trajectory" testid="scenario-chart">
            <div style={{ height: 280, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={results.series} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="rgb(var(--border))" strokeDasharray="2 4" />
                  <XAxis dataKey={Object.keys(results.series[0])[0]} tick={tickStyle} stroke="rgb(var(--border-strong))" />
                  <YAxis tick={tickStyle} stroke="rgb(var(--border-strong))" tickFormatter={v => typeof v === "number" && v > 1e6 ? `₱${(v / 1e6).toFixed(0)}M` : Math.round(v).toLocaleString()} />
                  <Tooltip content={<TT formatter={v => v.toLocaleString()} />} cursor={{ stroke: "rgb(var(--border-strong))" }} />
                  {Object.keys(results.series[0]).filter(k => k !== Object.keys(results.series[0])[0]).map((k, i) => (
                    <Area key={k} type="monotone" dataKey={k} stroke={C.chart[i % 5]} fill={C.chartAlpha((i % 5) + 1, 0.22)} strokeWidth={2.2} isAnimationActive={false} />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Tile>

          {/* Narrative */}
          <Tile title="AI Insight" action={<Badge status="info">Narrative</Badge>}>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-sm bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Icons.Sparkles size={16} />
              </div>
              <p className="text-sm leading-relaxed text-fg/90">{results.narrative}</p>
            </div>
          </Tile>
        </div>
      </div>
    </div>
  );
}
